'use client';

import type { PortalLoginSchemaType } from 'src/auth/schema';

import { useState } from 'react';
import { signIn } from 'next-auth/react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { useBoolean } from 'minimal-shared/hooks';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { Logo } from 'src/components/logo';
import { Iconify } from 'src/components/iconify';
import { Form, Field } from 'src/components/hook-form';

import { portalLoginSchema } from 'src/auth/schema';
import { FormHead } from 'src/auth/components/form-head';
import { getAuthErrorMessage } from 'src/auth/error-message';
import { PORTAL_PASSWORD_MIN_LENGTH } from 'src/auth/constants';

// ----------------------------------------------------------------------

type PortalLoginViewProps = {
  title?: string;
  callbackUrl?: string;
  errorMessage?: string | null;
  statusMessage?: string | null;
};

export function PortalLoginView({
  title = 'Inloggen in het klantenportaal',
  callbackUrl = paths.dashboard.root,
  errorMessage = null,
  statusMessage = null,
}: PortalLoginViewProps) {
  const router = useRouter();
  const showPassword = useBoolean();
  const [authErrorMessage, setAuthErrorMessage] = useState<string | null>(errorMessage);

  const methods = useForm<PortalLoginSchemaType>({
    resolver: zodResolver(portalLoginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    setAuthErrorMessage(null);

    const result = await signIn('credentials', {
      email: data.email,
      password: data.password,
      redirect: false,
      redirectTo: callbackUrl,
    });

    if (result?.error) {
      setAuthErrorMessage(
        getAuthErrorMessage(result.error, result.code) ??
          'Aanmelden lukt op dit moment niet. Controleer je gegevens en probeer opnieuw.'
      );
      return;
    }

    router.replace(callbackUrl);
    router.refresh();
  });

  return (
    <>
      <FormHead
        icon={<Logo isSingle={false} disabled sx={{ width: 176, height: 42 }} />}
        title={title}
        description="Log in met je emailadres en wachtwoord om de beveiligde omgeving van VlamSafe te openen."
      />

      {authErrorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {authErrorMessage}
        </Alert>
      )}

      {statusMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {statusMessage}
        </Alert>
      )}

      <Form methods={methods} onSubmit={onSubmit}>
        <Stack spacing={3}>
          <Field.Text
            name="email"
            label="E-mailadres"
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Box sx={{ gap: 1.5, display: 'flex', flexDirection: 'column' }}>
            <Link
              component={RouterLink}
              href={paths.forgotPassword}
              variant="body2"
              color="inherit"
              sx={{ alignSelf: 'flex-end' }}
            >
              Wachtwoord vergeten?
            </Link>

            <Field.Text
              name="password"
              label="Wachtwoord"
              placeholder={`Minimaal ${PORTAL_PASSWORD_MIN_LENGTH} tekens`}
              type={showPassword.value ? 'text' : 'password'}
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={showPassword.onToggle} edge="end">
                        <Iconify
                          icon={showPassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          <Stack spacing={1.5}>
            <Button
              fullWidth
              color="primary"
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
              loadingIndicator="Bezig met aanmelden..."
            >
              Inloggen
            </Button>
          </Stack>
        </Stack>
      </Form>
    </>
  );
}
